import { useEffect, useState } from "react";
import { Button, DatePicker, Select, TimePicker, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { CalendarDays, Clock } from "lucide-react";
import dayjs, { Dayjs } from "dayjs";
import viVN from "antd/es/date-picker/locale/vi_VN";
import enUS from "antd/es/date-picker/locale/en_US";
import { useTranslation } from "react-i18next";
import { mixPutV1 } from "services/mixin";
import { getBranches } from "../api/Branch";
import { filterOptionVietnamese } from "../lib/utils";

/**
 * Khối "Thời gian lịch hẹn" dùng chung cho các trang chi tiết lịch hẹn / lịch tư vấn /
 * lịch thực hiện. Xem thì hiển thị ngày - giờ - cơ sở, bấm "Chỉnh sửa" để đổi rồi lưu qua
 * PUT /api/v1/be/appointments/{id} (scheduled_at + branch_id, kèm customer_id bắt buộc).
 */

type Props = {
    appointmentId?: number | string | null;
    appointment: any;
    canEdit?: boolean;
    /** Trả lại phần dữ liệu vừa đổi để trang cha cập nhật state của nó */
    onUpdated?: (patch: { scheduled_at: string; branch_id: number | null; branch: any }) => void;
};

export default function AppointmentScheduleBox({
    appointmentId,
    appointment,
    canEdit = true,
    onUpdated,
}: Props) {
    const { i18n } = useTranslation();
    const datePickerLocale = i18n.language === "en" ? enUS : viVN;
    const dateDisplayFormat = i18n.language === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY";

    const [branchOptions, setBranchOptions] = useState<{ value: number; label: string }[]>([]);
    const [editing, setEditing] = useState(false);
    const [date, setDate] = useState<Dayjs | null>(null);
    const [time, setTime] = useState<Dayjs | null>(null);
    const [branchId, setBranchId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let ignore = false;
        getBranches({ page: 1 })
            .then((res: any) => {
                if (ignore) return;
                const items = Array.isArray(res?.data?.items)
                    ? res.data.items
                    : Array.isArray(res?.data)
                        ? res.data
                        : [];
                setBranchOptions(items.map((b: any) => ({ value: b.id, label: b.name })));
            })
            .catch(() => { });
        return () => { ignore = true; };
    }, []);

    const openEdit = () => {
        const at = appointment?.scheduled_at ? dayjs(appointment.scheduled_at) : null;
        setDate(at);
        setTime(at);
        setBranchId(appointment?.branch_id ?? null);
        setEditing(true);
    };

    const handleSave = async () => {
        if (!appointmentId || !appointment) return;
        if (!date || !time) {
            message.warning("Vui lòng chọn đủ ngày hẹn và giờ hẹn");
            return;
        }
        setSaving(true);
        try {
            // Ghép ngày ở ô trên với giờ ở ô dưới thành scheduled_at đúng định dạng API
            const scheduledAt = date
                .hour(time.hour())
                .minute(time.minute())
                .second(0)
                .format("YYYY-MM-DD HH:mm:ss");

            const res = await mixPutV1(`/api/v1/be/appointments/${appointmentId}`, {
                customer_id: appointment.customer_id ?? appointment.channel_customer_id,
                scheduled_at: scheduledAt,
                ...(branchId != null ? { branch_id: branchId } : {}),
            });
            if (!res?.status) throw new Error(res?.msg || "Cập nhật thất bại");

            const branch = branchOptions.find((b) => b.value === branchId);
            onUpdated?.({
                scheduled_at: scheduledAt,
                branch_id: branchId ?? appointment.branch_id ?? null,
                branch: branch ? { ...appointment.branch, id: branch.value, name: branch.label } : appointment.branch,
            });
            setEditing(false);
            message.success(res?.msg || "Cập nhật thời gian lịch hẹn thành công");
        } catch (error: any) {
            message.error(error?.message || "Cập nhật thời gian lịch hẹn thất bại");
        } finally {
            setSaving(false);
        }
    };

    const branchName =
        appointment?.branch?.name
        || branchOptions.find((b) => b.value === appointment?.branch_id)?.label
        || "—";

    return (
        // mb-6 cho khớp các thẻ khác trong cột phải, không thì dính sát thẻ hình ảnh bên dưới
        <div className="bg-white dark:bg-[#1f1f1f] rounded-xl shadow-sm border border-slate-100 dark:border-[#2a2a2a] p-6 mb-6 text-000000e0">
            <div className="flex items-center justify-between mb-4">
                <div className="title-info gap-2 text-sm font-bold text-slate-400 uppercase tracking-wider flex items-center">
                    <CalendarDays size={16} className="text-[#4e91ab]" />
                    Thời gian lịch hẹn
                </div>
                {canEdit && !editing && (
                    <button
                        onClick={openEdit}
                        className="text-[#3e91ab] hover:underline text-xs font-semibold flex items-center gap-1"
                    >
                        <EditOutlined /> Chỉnh sửa
                    </button>
                )}
            </div>

            {editing ? (
                <div className="flex flex-col gap-3">
                    <div>
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Chọn ngày</label>
                        <DatePicker
                            className="w-full"
                            value={date}
                            onChange={(d) => setDate(d)}
                            locale={datePickerLocale}
                            format={dateDisplayFormat}
                            allowClear={false}
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Chọn giờ</label>
                        <TimePicker
                            className="w-full"
                            value={time}
                            onChange={(v) => setTime(v)}
                            format="HH:mm"
                            minuteStep={5}
                            allowClear={false}
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Cơ sở</label>
                        <Select
                            className="w-full"
                            placeholder="Chọn cơ sở"
                            value={branchId ?? undefined}
                            options={branchOptions}
                            onChange={(v) => setBranchId(v)}
                            showSearch
                            filterOption={filterOptionVietnamese}
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button size="small" disabled={saving} onClick={() => setEditing(false)}>
                            Hủy
                        </Button>
                        <Button type="primary" size="small" loading={saving} onClick={handleSave}>
                            Lưu thay đổi
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="bg-slate-50 dark:bg-[#262626] rounded-lg p-4 flex flex-col gap-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Ngày hẹn</span>
                        <span className="flex items-center gap-1.5 font-medium">
                            <CalendarDays size={14} className="text-slate-400" />
                            {appointment?.scheduled_at ? dayjs(appointment.scheduled_at).format("DD/MM/YYYY") : "—"}
                        </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Giờ hẹn</span>
                        <span className="flex items-center gap-1.5 font-medium">
                            <Clock size={14} className="text-slate-400" />
                            {appointment?.scheduled_at ? dayjs(appointment.scheduled_at).format("HH:mm") : "—"}
                        </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Cơ sở</span>
                        <span className="font-medium text-right">{branchName}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
